/**
 * Landing Page
 * Public landing page for SciDraft - shown to unauthenticated users
 * Similar to Overleaf's landing page design
 */

import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuthStore } from '@stores/authStore'
import {
  DocumentTextIcon,
  SparklesIcon,
  AcademicCapIcon,
  ChartBarIcon,
  BeakerIcon,
  BookOpenIcon,
  ArrowRightIcon,
  CheckIcon,
  CodeBracketIcon,
  CloudIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'

const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuthStore()

  // Determine if we're on the landing page domain (scidraft.com) or app domain (app.scidraft.com)
  const isLandingDomain = window.location.hostname === 'scidraft.com' || 
                          window.location.hostname === 'www.scidraft.com'
  const appDomain = 'https://app.scidraft.com'

  // Redirect authenticated users to home page
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (isLandingDomain) {
        // If on landing domain and authenticated, redirect to app
        window.location.href = `${appDomain}/home`
      } else {
        navigate('/home', { replace: true })
      }
    }
  }, [isAuthenticated, isLoading, navigate, isLandingDomain, appDomain])

  // Helper function to handle navigation/redirect
  const handleGetStarted = (path: string = '/register') => {
    if (isLandingDomain) {
      // Redirect to app domain
      window.location.href = `${appDomain}${path}`
    } else {
      // Navigate within same domain
      navigate(path)
    }
  }

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Don't render if authenticated (will redirect)
  if (isAuthenticated) {
    return null
  }

  const features = [
    {
      icon: DocumentTextIcon,
      title: 'LaTeX Editor',
      description: 'Full-featured LaTeX editor with live PDF preview and syntax highlighting',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: SparklesIcon,
      title: 'AI-Powered Writing',
      description: 'Get intelligent suggestions and assistance for your scientific writing',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: BookOpenIcon,
      title: 'Citation Management',
      description: 'Organize and insert citations seamlessly with BibTeX support',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: ChartBarIcon,
      title: 'Data Visualization',
      description: 'Create and embed charts, graphs, and scientific figures',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: BeakerIcon,
      title: 'Research Tools',
      description: 'Literature discovery, draft generation, and collaboration features',
      color: 'from-indigo-500 to-purple-500',
    },
    {
      icon: AcademicCapIcon,
      title: 'Academic Focus',
      description: 'Built specifically for researchers, scientists, and academics',
      color: 'from-teal-500 to-cyan-500',
    },
  ]

  const benefits = [
    'Real-time collaboration with your research team',
    'AI-powered writing assistance and suggestions',
    'Seamless LaTeX compilation and PDF preview',
    'Integrated citation library and BibTeX support',
    'Cloud-based - access your work from anywhere',
    'Free tier available with premium features',
  ]

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 overflow-y-auto">
      {/* Header/Navigation */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500/10 to-cyan-500/10 dark:from-teal-500/20 dark:to-cyan-500/20 border border-teal-500/20 dark:border-teal-400/30 shadow-sm overflow-hidden p-1.5">
                <img 
                  src="/logos/favicon.svg" 
                  alt="SciDraft Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap">SciDraft</span>
            </div>

            {/* Navigation Links */}
            <div className="hidden lg:flex items-center gap-8 xl:gap-10 ml-8 xl:ml-12">
              <a href="#features" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors whitespace-nowrap">
                Features
              </a>
              <a href="#pricing" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors whitespace-nowrap">
                Pricing
              </a>
              <a href="#about" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors whitespace-nowrap">
                About
              </a>
            </div>

            {/* Product Hunt Badge - Desktop */}
            <div className="hidden xl:flex items-center mx-6 flex-shrink-0">
              <a 
                href="https://www.producthunt.com/products/scidraft/reviews/new?utm_source=badge-product_review&utm_medium=badge&utm_source=badge-scidraft" 
                target="_blank" 
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img 
                  src="https://api.producthunt.com/widgets/embed-image/v1/product_review.svg?product_id=1138834&theme=light" 
                  alt="scidraft - A workspace for research writing, citations, and discovery | Product Hunt" 
                  className="h-9 w-auto"
                  style={{ maxWidth: '180px' }}
                />
              </a>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3 lg:gap-4 flex-shrink-0">
              <Link
                to="/login"
                className="px-3 lg:px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors whitespace-nowrap"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="px-4 lg:px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
              >
                Sign up
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {/* Main Heading */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-8"
            >
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-6">
                <span className="bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-500 bg-clip-text text-transparent">
                  Accelerate scientific writing
                </span>
              </h1>
            </motion.div>

            <p className="text-2xl md:text-3xl text-gray-600 dark:text-gray-300 mb-4 max-w-3xl mx-auto font-light">
              with <span className="font-semibold text-teal-600 dark:text-teal-400">SciDraft</span>
            </p>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
              The collaborative, online LaTeX editor with AI-powered assistance that anyone can use.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleGetStarted('/register')}
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 text-lg"
              >
                <span>Sign up for free</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleGetStarted('/login')}
                className="px-8 py-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-semibold rounded-xl shadow-lg hover:shadow-xl border-2 border-slate-200 dark:border-slate-700 transition-all duration-200 text-lg"
              >
                Sign in
              </motion.button>
            </div>

            {/* Demo Preview */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="mt-16 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              <div className="bg-slate-100 dark:bg-slate-700 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">SciDraft Editor</span>
                </div>
              </div>
              <div className="p-8">
                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-6 font-mono text-sm">
                  <div className="text-teal-600 dark:text-teal-400">\begin{'{'}document{'}'}</div>
                  <div className="text-gray-700 dark:text-gray-300 ml-4 mt-2">
                    <div className="text-blue-600 dark:text-blue-400">\title</div>
                    <div className="text-gray-800 dark:text-gray-200 ml-4">{'{'}My Research Paper{'}'}</div>
                    <div className="text-blue-600 dark:text-blue-400 mt-4">\author</div>
                    <div className="text-gray-800 dark:text-gray-200 ml-4">{'{'}Your Name{'}'}</div>
                    <div className="text-gray-500 dark:text-gray-500 mt-4">% AI-powered writing assistance available</div>
                    <div className="text-gray-500 dark:text-gray-500">% Real-time collaboration enabled</div>
                  </div>
                  <div className="text-teal-600 dark:text-teal-400 mt-4">\end{'{'}document{'}'}</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Everything you need for scientific writing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Powerful tools designed specifically for researchers and academics
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 dark:border-slate-700"
            >
              <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-slate-50 dark:bg-slate-800/50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Why choose SciDraft?
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                Join researchers worldwide who trust SciDraft for their scientific writing needs.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center mt-0.5">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-8 shadow-2xl">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <CodeBracketIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">LaTeX Support</h3>
                      <p className="text-teal-50 text-sm">Full LaTeX compilation and preview</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <SparklesIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">AI Assistance</h3>
                      <p className="text-teal-50 text-sm">Intelligent writing suggestions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <UserGroupIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Collaboration</h3>
                      <p className="text-teal-50 text-sm">Real-time editing with your team</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <CloudIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">Cloud Storage</h3>
                      <p className="text-teal-50 text-sm">Access your work from anywhere</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Choose the plan that works best for you. All plans include our core features.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Free Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="bg-white dark:bg-slate-800 rounded-2xl p-8 border-2 border-slate-200 dark:border-slate-700 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Free</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-gray-900 dark:text-white">$0</span>
              <span className="text-gray-600 dark:text-gray-400">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>50 daily credits</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>Up to 10 documents</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>Basic LaTeX editor</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>Cloud storage</span>
              </li>
            </ul>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleGetStarted('/register')}
              className="w-full px-6 py-3 bg-slate-200 dark:bg-slate-700 text-gray-900 dark:text-white font-semibold rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            >
              Get Started
            </motion.button>
          </motion.div>

          {/* Student Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl p-8 border-2 border-teal-400 shadow-2xl relative"
          >
            <div className="absolute top-4 right-4 bg-white text-teal-600 text-xs font-bold px-3 py-1 rounded-full">
              POPULAR
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Student</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">$7.99</span>
              <span className="text-teal-100">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-white">
                <CheckIcon className="w-5 h-5 text-white" />
                <span>1,000 monthly credits</span>
              </li>
              <li className="flex items-center gap-2 text-white">
                <CheckIcon className="w-5 h-5 text-white" />
                <span>Up to 100 documents</span>
              </li>
              <li className="flex items-center gap-2 text-white">
                <CheckIcon className="w-5 h-5 text-white" />
                <span>Unlimited LaTeX compilation</span>
              </li>
              <li className="flex items-center gap-2 text-white">
                <CheckIcon className="w-5 h-5 text-white" />
                <span>Up to 5 editors per document</span>
              </li>
              <li className="flex items-center gap-2 text-white">
                <CheckIcon className="w-5 h-5 text-white" />
                <span>AI-powered features</span>
              </li>
            </ul>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleGetStarted('/register')}
              className="w-full px-6 py-3 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-colors"
            >
              Start Free Trial
            </motion.button>
          </motion.div>

          {/* Researcher Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-white dark:bg-slate-800 rounded-2xl p-8 border-2 border-slate-200 dark:border-slate-700 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Researcher</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-gray-900 dark:text-white">$14.99</span>
              <span className="text-gray-600 dark:text-gray-400">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>2,000 monthly credits</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>Up to 1,000 documents</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>Unlimited LaTeX compilation</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>Up to 10 editors per document</span>
              </li>
              <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <CheckIcon className="w-5 h-5 text-teal-500" />
                <span>All AI features + priority support</span>
              </li>
            </ul>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleGetStarted('/register')}
              className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-colors"
            >
              Start Free Trial
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-slate-50 dark:bg-slate-800/50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              About SciDraft
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Built by researchers, for researchers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                SciDraft was created to make scientific writing more accessible, collaborative, and efficient. 
                We believe that researchers should focus on their research, not struggle with complex tools.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                Our platform combines the power of LaTeX with modern collaboration features and AI assistance, 
                making it easier than ever to write, edit, and publish scientific documents.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Built for Collaboration
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Real-time editing, comments, and version control make it easy to work with your research team.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  AI-Powered Assistance
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Get intelligent suggestions for writing, citations, and formatting to accelerate your workflow.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Cloud-Based Platform
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Access your work from anywhere, on any device. No installation required.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 rounded-3xl p-12 md:p-16 text-center overflow-hidden"
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to start writing?
            </h2>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Join researchers worldwide who use SciDraft to create, collaborate, and publish their work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleGetStarted('/register')}
                className="px-10 py-4 bg-white text-teal-600 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 justify-center"
              >
                <span>Sign up for free</span>
                <ArrowRightIcon className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleGetStarted('/login')}
                className="px-10 py-4 bg-white/10 text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/20 transition-all duration-200"
              >
                Sign in
              </motion.button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">SciDraft</span>
              </div>
              <p className="text-sm">
                The modern scientific writing platform for researchers and academics.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-teal-400 transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-teal-400 transition-colors">Pricing</a></li>
                <li><Link to="/register" className="hover:text-teal-400 transition-colors">Sign up</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-teal-400 transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Templates</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Help Center</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#about" className="hover:text-teal-400 transition-colors">About</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-teal-400 transition-colors">Privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} SciDraft. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .dark .bg-grid-pattern {
          background-image: 
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        }
      `}</style>
    </div>
  )
}

export default LandingPage

